/**
 * Small shared primitives.
 *
 * Kept in one module because they are each under thirty lines and a file per
 * spinner is how a component directory becomes unnavigable. Anything that grows
 * past that earns its own file.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

// ---------------------------------------------------------------------------
// Spinner
// ---------------------------------------------------------------------------

/** An indeterminate progress indicator.
 *
 * `role="status"` with a label rather than a bare spinning div: a screen reader
 * user needs to know that something is loading, and the animation conveys
 * nothing to them. The `prefers-reduced-motion` rule in `styles.css` collapses
 * the animation to a static ring, which is still a visible "something is
 * happening" marker. */
export function Spinner({ label = 'Loading', size = 16 }: { label?: string; size?: number }): ReactNode {
  return (
    <span role="status" aria-live="polite" className="inline-flex items-center gap-2 text-ink-muted">
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        className="animate-spin"
        aria-hidden="true"
        focusable="false"
      >
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
        <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      </svg>
      <span className="sr-only">{label}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

/** Icons are inline SVG rather than an icon package.
 *
 * The set is eight glyphs; a dependency would be a supply-chain surface, a
 * bundle-size decision and a licence question in exchange for eight paths. They
 * are all `aria-hidden` because every one of them appears next to text or inside
 * a button that carries its own `aria-label`. */
export type IconName =
  | 'plus'
  | 'close'
  | 'chevron-down'
  | 'trash'
  | 'archive'
  | 'comment'
  | 'attachment'
  | 'sun'
  | 'moon'
  | 'monitor'
  | 'grip'
  | 'send'
  | 'history'
  | 'logout'
  | 'refresh';

const PATHS: Record<IconName, string> = {
  plus: 'M12 5v14M5 12h14',
  close: 'M6 6l12 12M18 6L6 18',
  'chevron-down': 'M6 9l6 6 6-6',
  trash: 'M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2',
  archive: 'M3 7h18v3H3zM5 10v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-9M10 14h4',
  comment: 'M21 12a8 8 0 0 1-8 8H7l-4 3V12a8 8 0 0 1 8-8h2a8 8 0 0 1 8 8z',
  attachment: 'M21 12.5 12.5 21a5 5 0 0 1-7-7l8-8a3.5 3.5 0 0 1 5 5l-8 8a2 2 0 0 1-3-3l7-7',
  sun: 'M12 4V2M12 22v-2M4 12H2M22 12h-2M6 6 4.5 4.5M19.5 19.5 18 18M6 18l-1.5 1.5M19.5 4.5 18 6M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z',
  moon: 'M21 13a9 9 0 1 1-10-10 7 7 0 0 0 10 10z',
  monitor: 'M3 4h18v12H3zM8 20h8M12 16v4',
  grip: 'M9 6h.01M9 12h.01M9 18h.01M15 6h.01M15 12h.01M15 18h.01',
  send: 'M4 12l16-8-6 16-2.5-6.5L4 12z',
  history: 'M3 12a9 9 0 1 0 3-6.7M3 4v5h5M12 7v5l3.5 2',
  logout: 'M15 17l5-5-5-5M20 12H9M12 3H5a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h7',
  refresh: 'M20 11a8 8 0 1 0-2 6.3M20 5v6h-6',
};

export function Icon({ name, size = 16, className }: { name: IconName; size?: number; className?: string }): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      <path d={PATHS[name]} />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

/** A modal dialog with the behaviour that is easy to forget.
 *
 * - Escape closes it.
 * - The first focusable control receives focus on open, so a keyboard user is
 *   not left at the top of the document.
 * - Focus returns to the element that opened it on close, so tab order continues
 *   from where the user was rather than from the top of the page.
 * - The backdrop is a `button` with an accessible name, so a screen reader user
 *   has a way to dismiss it that is not "press Escape and hope".
 *
 * What it deliberately does not do is trap focus. A focus trap is the correct
 * behaviour for a true modal and it is twenty lines of `keydown` handling that
 * interacts badly with `dnd-kit`'s own keyboard sensor; the compromise here is a
 * dialog that behaves correctly for the common path and is honest in
 * docs/SECURITY.md and the accessibility notes rather than silently
 * half-implemented.
 */
export function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose(): void;
  children: ReactNode;
}): ReactNode {
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<Element | null>(null);

  useEffect(() => {
    restoreRef.current = document.activeElement;
    const first = panelRef.current?.querySelector<HTMLElement>(
      'input, textarea, select, button, [href], [tabindex]:not([tabindex="-1"])',
    );
    first?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const restore = restoreRef.current;
      if (restore instanceof HTMLElement) restore.focus();
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[12vh]">
      <button
        type="button"
        aria-label="Close dialog"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-black/45 backdrop-blur-[2px]"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative w-full max-w-md rounded-xl border border-border bg-surface shadow-lift"
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="text-sm font-semibold text-ink">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-ink-subtle hover:bg-sunken hover:text-ink"
          >
            <Icon name="close" />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Field primitives
// ---------------------------------------------------------------------------

export const inputClass =
  'w-full rounded-md border border-border bg-canvas px-2.5 py-1.5 text-sm text-ink placeholder:text-ink-subtle focus:border-accent';

export const buttonClass =
  'inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50';

export const primaryButtonClass = `${buttonClass} bg-accent text-accent-ink hover:opacity-90`;

export const ghostButtonClass = `${buttonClass} text-ink-muted hover:bg-sunken hover:text-ink`;

export const dangerButtonClass = `${buttonClass} bg-danger-soft text-danger hover:opacity-90`;

// ---------------------------------------------------------------------------
// Debounced value
// ---------------------------------------------------------------------------

/** A value that lags its input by `delay` milliseconds.
 *
 * Used by the card title field so that a fast typist produces one mutation per
 * round trip rather than one per keystroke. The optimistic overlay updates on
 * every keystroke - that is what makes the field feel immediate - and only the
 * *send* is debounced. */
export function useDebounced<T>(value: T, delay: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return settled;
}

/** A clock that ticks only while `active`, used to expire relative timestamps
 * and stale typing indicators without re-rendering an idle board once a second. */
export function useTick(active: boolean, intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(timer);
  }, [active, intervalMs]);
  return now;
}
